import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import useMetaTags from '../hooks/useMetaTags';

const MakeupLanding = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const sliderRef = useRef(null);
  const autoRotateTimerRef = useRef(null);
  const startXRef = useRef(0);
  const scrollLeftRef = useRef(0);
  const isDraggingRef = useRef(false);

  useMetaTags({
    title: 'Manchester Makeup Artists | KTP Beauty Clinic',
    description: 'Book flawless glam, bridal artistry, and editorial makeup with KTP Beauty Clinic in Manchester. Tailored looks, on-location options, and artists you can trust.',
    canonicalUrl: 'https://ktpbeautyclinic.co.uk/makeup-services'
  });

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleDragStart = (e) => {
    isDraggingRef.current = true;
    startXRef.current = e.type === 'mousedown' ? e.pageX : e.touches[0].pageX;
    scrollLeftRef.current = sliderRef.current.scrollLeft;
    setIsPaused(true);
  };

  const handleDragMove = (e) => {
    if (!isDraggingRef.current) return;
    e.preventDefault();
    const x = e.type === 'mousemove' ? e.pageX : e.touches[0].pageX;
    const walk = x - startXRef.current;
    sliderRef.current.scrollLeft = scrollLeftRef.current - walk;
  };

  const handleDragEnd = () => {
    isDraggingRef.current = false;
    if (!sliderRef.current) return;
    const slideWidth = sliderRef.current.offsetWidth;
    const newSlide = Math.round(sliderRef.current.scrollLeft / slideWidth);
    setCurrentSlide(newSlide);
    sliderRef.current.scrollTo({
      left: newSlide * slideWidth,
      behavior: 'smooth'
    });

    setTimeout(() => setIsPaused(false), 5000);
  };

  useEffect(() => {
    const slider = sliderRef.current;
    if (!slider) return;

    slider.addEventListener('touchstart', handleDragStart);
    slider.addEventListener('touchmove', handleDragMove, { passive: false });
    slider.addEventListener('touchend', handleDragEnd);
    slider.addEventListener('mousedown', handleDragStart);
    slider.addEventListener('mousemove', handleDragMove);
    slider.addEventListener('mouseup', handleDragEnd);
    slider.addEventListener('mouseleave', handleDragEnd);

    autoRotateTimerRef.current = setInterval(() => {
      if (isPaused || !sliderRef.current) return;

      setCurrentSlide(previousSlide => {
        const nextIndex = (previousSlide + 1) % 3;
        sliderRef.current.scrollTo({
          left: nextIndex * sliderRef.current.offsetWidth,
          behavior: 'smooth'
        });
        return nextIndex;
      });
    }, 5000);

    return () => {
      slider.removeEventListener('touchstart', handleDragStart);
      slider.removeEventListener('touchmove', handleDragMove);
      slider.removeEventListener('touchend', handleDragEnd);
      slider.removeEventListener('mousedown', handleDragStart);
      slider.removeEventListener('mousemove', handleDragMove);
      slider.removeEventListener('mouseup', handleDragEnd);
      slider.removeEventListener('mouseleave', handleDragEnd);

      if (autoRotateTimerRef.current) {
        clearInterval(autoRotateTimerRef.current);
      }
    };
  }, [isPaused]);

  const handleDotClick = (index) => {
    if (!sliderRef.current) return;
    setCurrentSlide(index);
    setIsPaused(true);
    sliderRef.current.scrollTo({
      left: index * sliderRef.current.offsetWidth,
      behavior: 'smooth'
    });
    setTimeout(() => setIsPaused(false), 5000);
  };

  return (
    <div className="App makeup-landing">
      <header className={`App-header ${isScrolled ? 'scrolled' : ''}`}>
        <div className="left-section">
          <Link to="/" className="logo-link">
            <img src="/images/headerlogo.png" alt="KTP Beauty Clinic logo" className="logo" />
          </Link>
          <nav className="main-nav">
            <Link to="/" className="nav-link">Home</Link>
            <details className="nav-dropdown">
              <summary className="nav-link dropdown-toggle active">Services</summary>
              <div className="dropdown-menu">
                <Link to="/makeup-services" className="dropdown-link active">Makeup</Link>
              </div>
            </details>
            <Link to="/contact" className="nav-link">Contact</Link>
          </nav>
        </div>
        <div className="right-section">
          <a
            href="https://app.acuityscheduling.com/schedule/fe86af80"
            target="_blank"
            rel="noopener noreferrer"
            className="book-now"
          >
            Book Now
          </a>
          <a
            href="https://www.instagram.com/ktpbeautyclinic/"
            target="_blank"
            rel="noopener noreferrer"
            className="instagram-link"
          >
            <img src="/images/instagram.svg" alt="Instagram" />
          </a>
        </div>
        <div className="mobile-icons">
          <Link to="/contact" className="icon-link" aria-label="Message us">
            <img src="/images/message-icon.svg" alt="" className="icon" />
          </Link>
          <a
            href="https://app.acuityscheduling.com/schedule/fe86af80"
            target="_blank"
            rel="noopener noreferrer"
            className="icon-link"
          >
            <img src="/images/calendar-icon.svg" alt="Book" className="icon" />
          </a>
          <a
            href="https://www.instagram.com/ktpbeautyclinic/"
            target="_blank"
            rel="noopener noreferrer"
            className="icon-link"
          >
            <img src="/images/instagram.svg" alt="Instagram" className="icon" />
          </a>
          <a href="tel:+44161494392" className="icon-link">
            <img src="/images/phone-icon.svg" alt="Call Us" className="icon" />
          </a>
        </div>
      </header>

      <section className="hero-section">
        <div className="hero-content">
          <h2>Manchester Makeup Artists</h2>
          <h1>Flawless glam<br />for your big moment</h1>
          <p className="services">Soft glam • Bridal artistry • Editorial looks • Party-ready makeup • 1-1 lessons</p>
          <p>
            From bridal previews to red-carpet glam, our accredited artists create long-wear, camera-ready looks
            that still feel like you.
          </p>
          <a
            href="https://app.acuityscheduling.com/schedule/fe86af80"
            className="cta-button"
            target="_blank"
            rel="noopener noreferrer"
          >
            BOOK MAKEUP NOW
          </a>
          <p style={{ marginTop: '15px', fontSize: '1rem', opacity: '0.9' }}>
            Want to chat ideas first? Call the studio on{' '}
            <a href="tel:+441614946392" style={{ color: 'inherit', textDecoration: 'underline' }}>
              0161 494 6392
            </a>
          </p>
        </div>
      </section>

      <section className="about-section">
        <div className="about-content">
          <div className="about-text">
            <h2>Makeup that moves with you</h2>
            <p>
              Whether you are walking down the aisle, stepping on stage or prepping for a brand shoot, we build your
              look around your skin, schedule and vibe. Expect picture-perfect coverage that still feels breathable,
              with shades custom-blended to your complexion.
            </p>
            <ul>
              <li>Complimentary pre-event skin prep guidance to keep your canvas glowing.</li>
              <li>Hand-selected premium brands chosen for longevity, flash-proof finishes and sensitive skin.</li>
              <li>On-location glam available across Greater Manchester, Cheshire and beyond.</li>
            </ul>
          </div>
          <div className="about-image">
            <img src="/images/katie.jpg" alt="Makeup artist at KTP Beauty Clinic" />
          </div>
        </div>
      </section>

      <section className="about-section">
        <div className="about-content">
          <div className="about-image about-image--left">
            <img src="/images/bridal.png" alt="Bridal makeup look by KTP Beauty Clinic" />
          </div>
          <div className="about-text">
            <h2>Packages for every celebration</h2>
            <p>
              Our studio is set up for solo transformations, bridal parties and backstage teams. Choose in-clinic
              appointments, mobile glam or a custom quote for your production day.
            </p>
            <p>
              Need makeup artists who can stay for touch-ups, manage a full schedule or work alongside photographers?
              We can build a bespoke plan that keeps everyone photo-ready and on time.
            </p>
          </div>
        </div>
      </section>

      <section className="testimonials-section">
        <div className="testimonials-container">
          <h2>Glam that survives the dance floor</h2>
          <div className="testimonials-slider" ref={sliderRef}>
            <div className="testimonial">
              <p className="testimonial-text">
                "My wedding makeup was everything I hoped for. Katie nailed the natural glam brief, and it lasted all
                day through happy tears, hugs and a full night of dancing."
              </p>
              <div className="testimonial-stars">★★★★★</div>
              <p className="testimonial-author">Sophie McNeill</p>
              <p className="testimonial-source">BRIDAL MAKEUP | MANCHESTER</p>
            </div>
            <div className="testimonial">
              <p className="testimonial-text">
                "Booked a group glam session for my birthday and every single look was different but flawless. The
                girls knew exactly how to make us all feel incredible."
              </p>
              <div className="testimonial-stars">★★★★★</div>
              <p className="testimonial-author">Alexis Harper</p>
              <p className="testimonial-source">PARTY GLAM | STOCKPORT</p>
            </div>
            <div className="testimonial">
              <p className="testimonial-text">
                "We use KTP Beauty for brand shoots because they just get it. They keep the talent camera ready and
                understand how makeup reads on set lighting."
              </p>
              <div className="testimonial-stars">★★★★★</div>
              <p className="testimonial-author">Jodie Quinn</p>
              <p className="testimonial-source">COMMERCIAL PROJECT | AGENCY CLIENT</p>
            </div>
          </div>
          <div className="testimonial-navigation">
            {[0, 1, 2].map((index) => (
              <button
                key={index}
                className={`nav-dot ${currentSlide === index ? 'active' : ''}`}
                onClick={() => handleDotClick(index)}
              />
            ))}
          </div>
        </div>
      </section>

      <section className="cta-section">
        <h2>Ready for the glow-up?</h2>
        <p>
          Reserve your glam online in seconds, or drop us a message to lock in your date. We can hold provisional
          bridal bookings while you finalise timings.
        </p>
        <div className="cta-buttons">
          <a
            href="https://app.acuityscheduling.com/schedule/fe86af80"
            className="cta-section-button"
            target="_blank"
            rel="noopener noreferrer"
          >
            Secure Your Slot
          </a>
          <a href="mailto:ktpbeautyclinic@outlook.com" className="cta-section-button outline">
            Request a Call Back
          </a>
        </div>
      </section>

      <footer className="footer">
        <div className="footer-content">
          <div className="footer-section">
            <h4>Contact</h4>
            <p>
              126 Higher Bents Ln
              <br />
              Bredbury, Stockport
              <br />
              SK6 2LU
            </p>
            <p>
              <a href="tel:+44161494392">+44 161 494 6392</a>
            </p>
          </div>

          <div className="footer-section">
            <h4>Opening Hours</h4>
            <p>
              Our typical hours are listed below, but it's always best to check in before hand, or book an appointment
              online.
            </p>
            <p>
              Monday - Saturday: 10am - 7pm
              <br />
              Sunday: Closed
            </p>
          </div>

          <div className="footer-section">
            <h4>Reviews</h4>
            <div className="rating">
              <p>
                <span className="stars">⭐⭐⭐⭐⭐</span>
                <br />
                <span className="rating-link">
                  4.8 on Google Reviews
                </span>
              </p>
            </div>
            <a
              href="https://www.instagram.com/ktpbeautyclinic/"
              className="social-link"
              target="_blank"
              rel="noopener noreferrer"
            >
              <img src="/images/instagram.svg" alt="Follow us on Instagram" />
            </a>
          </div>
        </div>
        <div className="footer-copyright">
          <p>&copy; {new Date().getFullYear()} KTP Beauty Clinic. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default MakeupLanding;
